/**
 * Clarificação de logística de viagem (chegada / partida), antes de gerar.
 *
 * Regras vinculativas:
 * - máquina de estados local com uma única fonte de verdade (`stage`);
 * - cada botão tem um handler real que muda estado visível ou avança;
 * - nenhum clique aqui chama Places, Routes, Photos nem consome crédito:
 *   o componente só devolve texto ao pedido original;
 * - nunca inventa hotel, aeroporto, check-in ou bagagem;
 * - uma hora já reconhecida nunca volta a `undefined`.
 */

import { useEffect, useState } from "react";
import { BedDouble, Luggage, MapPin, Plane } from "lucide-react";
import { type TravelLogistics } from "@/lib/arrival-window";
import { airportPlaceholder } from "@/lib/travel-logistics-chain";
import {
  airportAnswer,
  baseLogisticsStage,
  chooseArrivalKind,
  chooseDepartureKind,
  hotelAnswer,
  knownTimeHHMM,
  type ArrivalChoice,
  type DepartureChoice,
  type LogisticsStage,
} from "@/lib/logistics-clarification-machine";
import { TimeInput } from "@/components/TimeInput";
import { cn } from "@/lib/utils";

export type LogisticsClarificationProps = {
  missing: "logistics_arrival" | "logistics_departure";
  logistics: TravelLogistics;
  lang: string;
  /** Destino atual, só para o placeholder genérico do aeroporto. */
  destination?: string | null;
  /** Existe um hotel realmente escolhido (com identidade). */
  hotelSelected?: boolean;
  /** Fluxo existente de escolha de hotel, quando disponível. */
  onChooseHotel?: () => void;
  /** Resposta em texto, para ser fundida no pedido original pelo chamador. */
  onAnswer: (answer: string) => void;
  /** "Ainda não sei — criar roteiro sem logística". */
  onSkipLogistics: () => void;
};

const T = {
  continue: { pt: "Continuar", en: "Continue", es: "Continuar", fr: "Continuer", it: "Continua", de: "Weiter", ja: "続ける" },
  arrivalWhere: {
    pt: "Onde ficas disponível?",
    en: "Where will you be available?",
    es: "¿Dónde quedas disponible?",
    fr: "Où seras-tu disponible ?",
    it: "Dove sarai disponibile?",
    de: "Wo bist du verfügbar?",
    ja: "どこから動けますか？",
  },
  airportChip: { pt: "Aterro no aeroporto", en: "Landing at the airport", es: "Aterrizo en el aeropuerto", fr: "J'atterris à l'aéroport", it: "Atterro all'aeroporto", de: "Landung am Flughafen", ja: "空港に着陸" },
  cityChip: { pt: "Já estou na cidade", en: "Already in the city", es: "Ya estoy en la ciudad", fr: "Déjà en ville", it: "Sono già in città", de: "Schon in der Stadt", ja: "もう街にいる" },
  hotelChip: { pt: "Chego ao hotel", en: "Arriving at the hotel", es: "Llego al hotel", fr: "J'arrive à l'hôtel", it: "Arrivo in hotel", de: "Ich komme im Hotel an", ja: "ホテルに到着" },
  arrivalAirportQuestion: {
    pt: "Em que aeroporto aterras?",
    en: "Which airport do you land at?",
    es: "¿En qué aeropuerto aterrizas?",
    fr: "À quel aéroport atterris-tu ?",
    it: "In quale aeroporto atterri?",
    de: "An welchem Flughafen landest du?",
    ja: "どの空港に着きますか？",
  },
  hotelQuestion: {
    pt: "Qual é o hotel? Sem hotel escolhido não criamos check-in.",
    en: "Which hotel? Without a chosen hotel we don't create a check-in.",
    es: "¿Qué hotel? Sin hotel elegido no creamos check-in.",
    fr: "Quel hôtel ? Sans hôtel choisi, pas de check-in.",
    it: "Quale hotel? Senza hotel scelto non creiamo il check-in.",
    de: "Welches Hotel? Ohne gewähltes Hotel gibt es kein Check-in.",
    ja: "どのホテルですか？未選択ならチェックインは作りません。",
  },
  chooseHotel: { pt: "Escolher hotel", en: "Choose hotel", es: "Elegir hotel", fr: "Choisir l'hôtel", it: "Scegli hotel", de: "Hotel wählen", ja: "ホテルを選ぶ" },
  skip: {
    pt: "Ainda não sei — criar roteiro sem logística",
    en: "I don't know yet — create the itinerary without logistics",
    es: "Aún no sé — crear itinerario sin logística",
    fr: "Je ne sais pas encore — créer l'itinéraire sans logistique",
    it: "Non lo so ancora — crea l'itinerario senza logistica",
    de: "Weiß ich noch nicht — Route ohne Logistik erstellen",
    ja: "まだ不明 — ロジ無しで作成",
  },
  departureKindQuestion: {
    pt: "É voo (aeroporto) ou só saída da cidade?",
    en: "Is it a flight (airport) or just leaving the city?",
    es: "¿Es un vuelo (aeropuerto) o solo salir de la ciudad?",
    fr: "Est-ce un vol (aéroport) ou juste quitter la ville ?",
    it: "È un volo (aeroporto) o solo lasciare la città?",
    de: "Ist es ein Flug (Flughafen) oder nur die Stadt verlassen?",
    ja: "飛行機ですか、街を出るだけですか？",
  },
  departureFlightChip: { pt: "Voo — aeroporto", en: "Flight — airport", es: "Vuelo — aeropuerto", fr: "Vol — aéroport", it: "Volo — aeroporto", de: "Flug — Flughafen", ja: "飛行機 — 空港" },
  departureCityChip: { pt: "Só saio da cidade", en: "Just leaving the city", es: "Solo salgo de la ciudad", fr: "Je quitte juste la ville", it: "Lascio solo la città", de: "Nur die Stadt verlassen", ja: "街を出るだけ" },
  back: { pt: "Voltar", en: "Back", es: "Volver", fr: "Retour", it: "Indietro", de: "Zurück", ja: "戻る" },
} as const;

function t(dict: Record<string, string>, lang: string): string {
  return dict[(lang || "pt").slice(0, 2).toLowerCase()] ?? dict.en ?? dict.pt;
}

export function LogisticsClarification({
  missing,
  logistics,
  lang,
  destination,
  hotelSelected,
  onChooseHotel,
  onAnswer,
  onSkipLogistics,
}: LogisticsClarificationProps) {
  const initial = baseLogisticsStage(missing, logistics);
  const [stage, setStage] = useState<LogisticsStage>(initial);
  const [text, setText] = useState("");
  const [time, setTime] = useState(knownTimeHHMM(missing, logistics) ?? "09:00");

  // Novo pedido/nova pergunta → repõe a máquina de estados; a hora conhecida
  // nunca é perdida nem substituída por undefined.
  useEffect(() => {
    setStage(() => baseLogisticsStage(missing, logistics));
    setText(() => "");
    const known = knownTimeHHMM(missing, logistics);
    if (known) setTime(() => known);
  }, [missing, logistics]);

  const arrivalHHMM = knownTimeHHMM("logistics_arrival", logistics);
  const knownArrivalAirport =
    logistics.arrival?.kind === "airport" ? (logistics.arrival.airportName ?? null) : null;
  const departureHHMM = knownTimeHHMM("logistics_departure", logistics);

  const applyArrivalChoice = (choice: ArrivalChoice) => {
    const next = chooseArrivalKind(choice, { arrivalHHMM, hotelSelected, knownAirport: knownArrivalAirport });
    if (next.type === "answer") {
      onAnswer(next.answer);
      return;
    }
    if (next.openHotelPicker) onChooseHotel?.();
    setStage(() => next.stage);
  };

  const chipClass = (active: boolean) =>
    cn(
      "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition",
      active ? "bg-primary text-primary-foreground" : "border border-border bg-background text-foreground hover:border-foreground/40",
    );

  if (stage === "arrival_time" || stage === "departure_time") {
    return (
      <div className="mt-2 flex min-w-0 flex-col gap-3" data-testid="logistics-time">
        <TimeInput value={time} onChange={setTime} ariaLabel={stage === "arrival_time" ? "arrival" : "departure"} />
        <button
          type="button"
          data-testid="logistics-time-continue"
          onClick={() => onAnswer(time)}
          className="w-fit rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background"
        >
          {t(T.continue, lang)}
        </button>
      </div>
    );
  }

  if (stage === "arrival_kind") {
    return (
      <div className="mt-2 flex min-w-0 flex-col gap-3" data-testid="logistics-arrival-kind">
        {arrivalHHMM && (
          <p className="text-xs text-muted-foreground" data-testid="logistics-known-time">
            {arrivalHHMM} · {t(T.arrivalWhere, lang)}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="chip-arrival-airport"
            className={chipClass(false)}
            onClick={() => applyArrivalChoice("airport")}
          >
            <Plane className="h-3.5 w-3.5" />
            {t(T.airportChip, lang)}
          </button>
          <button
            type="button"
            data-testid="chip-arrival-city"
            className={chipClass(false)}
            onClick={() => applyArrivalChoice("city")}
          >
            <MapPin className="h-3.5 w-3.5" />
            {t(T.cityChip, lang)}
          </button>
          <button
            type="button"
            data-testid="chip-arrival-hotel"
            className={chipClass(false)}
            onClick={() => applyArrivalChoice("hotel")}
          >
            <BedDouble className="h-3.5 w-3.5" />
            {t(T.hotelChip, lang)}
          </button>
        </div>
        <button
          type="button"
          data-testid="logistics-skip"
          onClick={onSkipLogistics}
          className="inline-flex w-fit items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:border-foreground/40"
        >
          <Luggage className="h-3.5 w-3.5" />
          {t(T.skip, lang)}
        </button>
      </div>
    );
  }

  const applyDepartureChoice = (choice: DepartureChoice) => {
    const next = chooseDepartureKind(choice, { departureHHMM });
    if (next.type === "answer") onAnswer(next.answer);
    else setStage(() => next.stage);
  };

  if (stage === "departure_kind") {
    return (
      <div className="mt-2 flex min-w-0 flex-col gap-3" data-testid="logistics-departure-kind">
        {departureHHMM && (
          <p className="text-xs text-muted-foreground" data-testid="logistics-known-time">
            {departureHHMM} · {t(T.departureKindQuestion, lang)}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="chip-departure-flight"
            className={chipClass(false)}
            onClick={() => applyDepartureChoice("flight")}
          >
            <Plane className="h-3.5 w-3.5" />
            {t(T.departureFlightChip, lang)}
          </button>
          <button
            type="button"
            data-testid="chip-departure-city"
            className={chipClass(false)}
            onClick={() => applyDepartureChoice("city")}
          >
            <MapPin className="h-3.5 w-3.5" />
            {t(T.departureCityChip, lang)}
          </button>
        </div>
        <button
          type="button"
          data-testid="logistics-skip"
          onClick={onSkipLogistics}
          className="inline-flex w-fit items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:border-foreground/40"
        >
          <Luggage className="h-3.5 w-3.5" />
          {t(T.skip, lang)}
        </button>
      </div>
    );
  }

  if (stage === "hotel_selection") {
    return (
      <div className="mt-2 flex min-w-0 flex-col gap-2" data-testid="logistics-hotel-selection">
        <p className="text-xs text-muted-foreground">{t(T.hotelQuestion, lang)}</p>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="logistics-hotel-input"
          placeholder={t(T.chooseHotel, lang)}
          className="min-w-0 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground/40"
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="logistics-hotel-continue"
            disabled={!text.trim()}
            onClick={() => onAnswer(hotelAnswer(text, arrivalHHMM, knownArrivalAirport))}
            className="rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-50"
          >
            {t(T.continue, lang)}
          </button>
          <button
            type="button"
            data-testid="logistics-hotel-back"
            onClick={() => setStage(() => "arrival_kind")}
            className="rounded-xl border border-border px-4 py-2 text-sm font-medium"
          >
            {t(T.back, lang)}
          </button>
        </div>
      </div>
    );
  }

  // arrival_airport | departure_airport
  const isDeparture = stage === "departure_airport";
  return (
    <div className="mt-2 flex min-w-0 flex-col gap-2" data-testid={isDeparture ? "logistics-departure-airport" : "logistics-arrival-airport"}>
      {!isDeparture && <p className="text-xs text-muted-foreground">{t(T.arrivalAirportQuestion, lang)}</p>}
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="logistics-airport-input"
        // Placeholder genérico do destino atual — nunca um aeroporto anterior.
        placeholder={airportPlaceholder(destination, lang)}
        className="min-w-0 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground/40"
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="logistics-airport-continue"
          disabled={!text.trim()}
          onClick={() => onAnswer(airportAnswer(text, { isDeparture, hhmm: isDeparture ? departureHHMM : arrivalHHMM }))}
          className="rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-50"
        >
          {t(T.continue, lang)}
        </button>
        <button
          type="button"
          data-testid="logistics-skip"
          onClick={onSkipLogistics}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:border-foreground/40"
        >
          <Luggage className="h-3.5 w-3.5" />
          {t(T.skip, lang)}
        </button>
      </div>
    </div>
  );
}
